/**
 * Audit Template Detail Page
 *
 * Detail view for CyberSweep audit templates.
 * Fetches data from Gobodo/RDS (Cyber_Sweep_Audit_Temp__c).
 * Shows Related and Details tabs matching Salesforce layout.
 */

import React, { useState } from 'react';
import {
  ChevronLeft,
  FileText,
  Globe,
  CheckCircle,
  XCircle,
  AlertCircle,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  X,
} from 'lucide-react';
import {
  useAuditTemplate,
  useAuditWebsitesByTemplateInfinite,
  useAuditWebsiteCountByTemplate,
  usePAWRecordsByTemplate,
  usePAWRecordCountByTemplate,
  GobodoAuditTemplate,
  PAWRecordDetail,
  AuditWebsiteRecord,
} from '../../services/queries/cyberSweepQueries';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';

interface AuditTemplateDetailProps {
  templateId: string;
  onBack: () => void;
  onAuditWebsiteClick?: (websiteId: string) => void;
  onPAWClick?: (pawId: string, csAuditId?: string) => void;
}

type TabType = 'related' | 'details';

const AuditTemplateDetail: React.FC<AuditTemplateDetailProps> = ({ templateId, onBack, onAuditWebsiteClick, onPAWClick }) => {
  const [activeTab, setActiveTab] = useState<TabType>('related');
  const [isEditing, setIsEditing] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Fetch from Gobodo/RDS
  const { data: gobodoTemplate, isLoading } = useAuditTemplate(templateId);

  // Flag for whether we have valid Gobodo data
  const useGobodoData = !!(USE_GOBODO_API && gobodoTemplate);

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-3">
        <button
          onClick={onBack}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back
        </button>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-green-600" />
          <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Loading...</span>
        </div>
      </div>
    );
  }

  // Not found state
  if (!gobodoTemplate) {
    return (
      <div className="space-y-3">
        <button
          onClick={onBack}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back
        </button>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
            Template Not Found
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            ID "{templateId}" not found.
          </p>
        </div>
      </div>
    );
  }

  // Get data from Gobodo (RDS) - no static fallback
  const displayName = gobodoTemplate?.name ?? '';
  const phase1Days = gobodoTemplate?.phase1DueDateDays ?? 7;
  const phase2Days = gobodoTemplate?.phase2DueDateDays ?? 0;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div>
        {/* Back button */}
        <button
          onClick={onBack}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 mb-2"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back
        </button>

        {/* Title + Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-green-100 dark:bg-green-900/30 rounded">
              <FileText className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Cyber Sweep Audit Template
              </span>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {displayName}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setShowNotes(!showNotes)}
              className={`px-2 py-1 text-xs font-medium border rounded transition-colors ${
                showNotes
                  ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600'
                  : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
              }`}
            >
              <FileText className="h-3.5 w-3.5 inline mr-1" />
              Note
            </button>
            <button
              onClick={() => {
                setIsEditing(!isEditing);
                if (!isEditing) setActiveTab('details');
              }}
              className={`px-2 py-1 text-xs font-medium border rounded transition-colors ${
                isEditing
                  ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600'
                  : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
              }`}
            >
              <Edit2 className="h-3.5 w-3.5 inline mr-1" />
              {isEditing ? 'Cancel' : 'Edit'}
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-2 py-1 text-xs font-medium text-red-600 dark:text-red-400 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <Trash2 className="h-3.5 w-3.5 inline mr-1" />
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Notes Section (collapsible) */}
      {showNotes && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800 p-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Add Note</h3>
            <button
              onClick={() => setShowNotes(false)}
              className="text-yellow-600 hover:text-yellow-800 dark:text-yellow-400 dark:hover:text-yellow-200"
            >
              <XCircle className="h-4 w-4" />
            </button>
          </div>
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Enter your note here..."
            className="w-full px-3 py-2 text-sm border border-yellow-300 dark:border-yellow-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-1 focus:ring-yellow-400 focus:border-yellow-400"
            rows={3}
          />
          <div className="mt-2 flex justify-end gap-2">
            <button
              onClick={() => {
                setNoteText('');
                setShowNotes(false);
              }}
              className="px-3 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                console.log('[AuditTemplate] Note saved:', noteText);
                // TODO: Save note via Gobodo API
                setNoteText('');
                setShowNotes(false);
              }}
              className="px-3 py-1 text-xs font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded"
            >
              Save Note
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Confirm Delete</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to delete "{displayName}"? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  console.log('[AuditTemplate] Delete confirmed for:', templateId);
                  // TODO: Implement delete via Gobodo API
                  setShowDeleteConfirm(false);
                  onBack();
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('related')}
            className={`py-1.5 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'related'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Related
          </button>
          <button
            onClick={() => setActiveTab('details')}
            className={`py-1.5 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'details'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Details
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'related' ? (
        <RelatedTab
          templateId={templateId}
          templateName={displayName}
          onAuditWebsiteClick={onAuditWebsiteClick}
          onPAWClick={(pawId) => onPAWClick?.(pawId)}
        />
      ) : (
        <DetailsTab
          gobodoData={gobodoTemplate}
          useGobodoData={useGobodoData}
          phase1Days={phase1Days}
          phase2Days={phase2Days}
          isEditing={isEditing}
          onSave={() => {
            console.log('[AuditTemplate] Save changes');
            // TODO: Save via Gobodo API
            setIsEditing(false);
          }}
        />
      )}
    </div>
  );
};

// New Audit Modal Component
interface NewAuditModalProps {
  templateId: string;
  templateName: string;
  onClose: () => void;
  onSave?: (auditData: NewAuditFormData) => void;
}

interface NewAuditFormData {
  clientId: string;
  startDate: string;
  notes: string;
}

const NewAuditModal: React.FC<NewAuditModalProps> = ({ templateId, templateName, onClose, onSave }) => {
  const [formData, setFormData] = useState<NewAuditFormData>({
    clientId: '',
    startDate: new Date().toISOString().split('T')[0],
    notes: '',
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    // TODO: Implement actual save to Gobodo
    console.log('Creating new audit:', { templateId, ...formData });

    // Simulate save delay
    await new Promise(resolve => setTimeout(resolve, 500));

    if (onSave) {
      onSave(formData);
    }

    setIsSaving(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">New CS Audit</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Template: {templateName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Client Lookup */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Client <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.clientId}
              onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
              placeholder="Search for client..."
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Enter client name or ID to search
            </p>
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Start Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Optional notes..."
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
          </div>

          {/* Template Info (read-only) */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-md p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">This audit will be created with:</p>
            <div className="text-sm text-gray-700 dark:text-gray-300">
              <span className="font-medium">Template:</span> {templateName}
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isSaving || !formData.clientId}
            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="h-3.5 w-3.5" />
                Create Audit
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Related Tab Component
interface RelatedTabProps {
  templateId: string;
  templateName: string;
  onViewAllAudits?: () => void;
  onAuditWebsiteClick?: (websiteId: string) => void;
  onPAWClick?: (pawId: string) => void;
  onViewAllPAWs?: () => void;
}

const RelatedTab: React.FC<RelatedTabProps> = ({ templateId, templateName, onAuditWebsiteClick, onPAWClick, onViewAllPAWs }) => {
  const [showNewAuditModal, setShowNewAuditModal] = useState(false);

  // Fetch Audit Websites from Cyber_Sweep_Audit_Website__c
  const {
    data: auditWebsitePages,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useAuditWebsitesByTemplateInfinite(templateId);
  const { data: auditWebsiteCount = 0 } = useAuditWebsiteCountByTemplate(templateId);

  // Fetch PAW Records for this template
  const {
    data: pawRecords = [] as PAWRecordDetail[],
    isLoading: pawLoading,
  } = usePAWRecordsByTemplate(templateId);
  const { data: pawRecordCount = 0 } = usePAWRecordCountByTemplate(templateId);

  const websites: AuditWebsiteRecord[] = auditWebsitePages?.pages.flatMap((page: { records: AuditWebsiteRecord[] }) => page.records) ?? [];
  const displayCount = String(auditWebsiteCount || websites.length);

  const pawDisplayCount = pawRecordCount > 0 ? `${pawRecordCount}+` : String(pawRecords.length);

  return (
    <div className="space-y-3">
      {/* New Audit Modal */}
      {showNewAuditModal && (
        <NewAuditModal
          templateId={templateId}
          templateName={templateName}
          onClose={() => setShowNewAuditModal(false)}
        />
      )}

      {/* Audit Name Related List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        {/* Header - Audit Name */}
        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-3.5 w-3.5 text-gray-400" />
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Audit Name</h3>
            {isLoading ? (
              <Loader2 className="h-3 w-3 animate-spin text-gray-400" />
            ) : (
              <span className="text-xs text-gray-500">({displayCount})</span>
            )}
          </div>
          <button
            onClick={() => setShowNewAuditModal(true)}
            className="px-2.5 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md shadow-sm flex items-center gap-1"
          >
            <Plus className="h-3.5 w-3.5" />
            New
          </button>
        </div>

        {/* Subsection - Audit Website ID */}
        <div className="px-3 py-1.5 bg-gray-50 dark:bg-gray-900/30 border-b border-gray-200 dark:border-gray-700">
          <span className="text-xs text-gray-500 dark:text-gray-400">Audit Website ID</span>
        </div>

        {/* Audit Website Records */}
        {isLoading ? (
          <div className="px-3 py-4 text-center">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400 mx-auto" />
            <span className="text-xs text-gray-500 mt-1 block">Loading audit websites...</span>
          </div>
        ) : websites.length === 0 ? (
          <div className="px-3 py-3 text-center text-xs text-gray-500 dark:text-gray-400">
            No audit websites found for this template
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {websites.map((website) => (
                <div
                  key={website.id}
                  onClick={() => onAuditWebsiteClick?.(website.key || website.id)}
                  className="px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                >
                  <span className="text-sm text-blue-600 hover:underline dark:text-blue-400">
                    {website.name || website.id}
                  </span>
                </div>
              ))}
            </div>

            {/* View All footer */}
            {hasNextPage && (
              <div className="px-3 py-1.5 border-t border-gray-200 dark:border-gray-700 text-center">
                <button
                  onClick={() => { void fetchNextPage(); }}
                  disabled={isFetchingNextPage}
                  className="text-xs text-blue-600 hover:underline dark:text-blue-400 disabled:text-gray-400 disabled:no-underline"
                >
                  {isFetchingNextPage ? 'Loading...' : 'View All'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Cyber Sweep Audit Categories Related List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe className="h-3.5 w-3.5 text-gray-400" />
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Audit Categories</h3>
            <span className="text-xs text-gray-500">(0)</span>
          </div>
          <button className="px-2.5 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md shadow-sm flex items-center gap-1">
            <Plus className="h-3.5 w-3.5" />
            New
          </button>
        </div>
        <div className="px-3 py-3 text-center text-xs text-gray-500 dark:text-gray-400">
          No audit categories found
        </div>
      </div>

      {/* Performed Audit Websites Related List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe className="h-3.5 w-3.5 text-purple-500" />
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Performed Audit Websites</h3>
            {pawLoading ? (
              <Loader2 className="h-3 w-3 animate-spin text-gray-400" />
            ) : (
              <span className="text-xs text-gray-500">({pawDisplayCount})</span>
            )}
          </div>
        </div>

        {/* Subsection - Performed Audit Website Name */}
        <div className="px-3 py-1.5 bg-gray-50 dark:bg-gray-900/30 border-b border-gray-200 dark:border-gray-700">
          <span className="text-xs text-gray-500 dark:text-gray-400">Performed Audit Website Name</span>
        </div>

        {/* PAW Records */}
        {pawLoading ? (
          <div className="px-3 py-4 text-center">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400 mx-auto" />
            <span className="text-xs text-gray-500 mt-1 block">Loading performed audit websites...</span>
          </div>
        ) : pawRecords.length === 0 ? (
          <div className="px-3 py-3 text-center text-xs text-gray-500 dark:text-gray-400">
            No performed audit websites found for this template
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {pawRecords.slice(0, 6).map((paw) => (
                <div
                  key={paw.id}
                  onClick={() => onPAWClick?.(paw.id)}
                  className="px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                >
                  <span className="text-sm text-blue-600 hover:underline dark:text-blue-400">
                    {paw.name || `PAW-${paw.id}`}
                  </span>
                </div>
              ))}
            </div>

            {/* View All footer */}
            {pawRecords.length >= 6 && (
              <div className="px-3 py-1.5 border-t border-gray-200 dark:border-gray-700 text-center">
                <button
                  onClick={() => onViewAllPAWs?.()}
                  className="text-xs text-blue-600 hover:underline dark:text-blue-400"
                >
                  View All
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Details Tab Component
interface DetailsTabProps {
  gobodoData: GobodoAuditTemplate | null | undefined;
  useGobodoData: boolean;
  phase1Days: number;
  phase2Days: number;
  isEditing: boolean;
  onSave: () => void;
}

const DetailsTab: React.FC<DetailsTabProps> = ({
  gobodoData,
  useGobodoData,
  phase1Days: initialPhase1Days,
  phase2Days: initialPhase2Days,
  isEditing,
  onSave,
}) => {
  const displayName = gobodoData?.name ?? '';

  // Editable state
  const [editPhase1Days, setEditPhase1Days] = useState(initialPhase1Days);
  const [editPhase2Days, setEditPhase2Days] = useState(initialPhase2Days);

  // Use edited values when editing, otherwise use initial
  const phase1Days = isEditing ? editPhase1Days : initialPhase1Days;
  const phase2Days = isEditing ? editPhase2Days : initialPhase2Days;

  // Safe date formatter that handles various formats (includes time)
  const safeFormatDate = (dateStr: string | undefined | null): string => {
    if (!dateStr) return '';
    try {
      // Try parsing as ISO date or timestamp
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  };

  return (
    <div className="space-y-3">
      {/* Details Card - no header banner */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="p-3">
          <div className="grid grid-cols-2 gap-x-8 gap-y-3">
            {/* Row 1: Cyber Sweep Audit Templates Name | Phase 1 Due Date Days */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Cyber Sweep Audit Templates Name</label>
              <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">{displayName || '-'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Phase 1 Due Date Days</label>
              {isEditing ? (
                <input
                  type="number"
                  value={editPhase1Days}
                  onChange={(e) => setEditPhase1Days(parseInt(e.target.value) || 0)}
                  min={1}
                  max={365}
                  className="mt-0.5 w-24 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              ) : (
                <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">{phase1Days}</p>
              )}
            </div>

            {/* Row 2: (empty) | Phase 2 Due Date Days */}
            <div>{/* Empty cell for alignment */}</div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Phase 2 Due Date Days</label>
              {isEditing ? (
                <input
                  type="number"
                  value={editPhase2Days}
                  onChange={(e) => setEditPhase2Days(parseInt(e.target.value) || 0)}
                  min={0}
                  max={365}
                  className="mt-0.5 w-24 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              ) : (
                <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">{phase2Days}</p>
              )}
            </div>

            {/* Row 3: Created By | Last Modified By */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Created By</label>
              <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">
                {useGobodoData && gobodoData?.createdByName ? (
                  <>
                    <span className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer">
                      {gobodoData.createdByName}
                    </span>
                    {safeFormatDate(gobodoData.createdDate) && (
                      <span className="text-gray-500 dark:text-gray-400 ml-1">
                        , {safeFormatDate(gobodoData.createdDate)}
                      </span>
                    )}
                  </>
                ) : (
                  '-'
                )}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Last Modified By</label>
              <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">
                {useGobodoData && gobodoData?.lastModifiedByName ? (
                  <>
                    <span className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer">
                      {gobodoData.lastModifiedByName}
                    </span>
                    {safeFormatDate(gobodoData.lastModifiedDate) && (
                      <span className="text-gray-500 dark:text-gray-400 ml-1">
                        , {safeFormatDate(gobodoData.lastModifiedDate)}
                      </span>
                    )}
                  </>
                ) : (
                  '-'
                )}
              </p>
            </div>
          </div>

          {/* Save Button when editing */}
          {isEditing && (
            <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => {
                  console.log('[AuditTemplate] Saving changes:', {
                    phase1Days: editPhase1Days,
                    phase2Days: editPhase2Days,
                  });
                  onSave();
                }}
                className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
              >
                Save Changes
              </button>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default AuditTemplateDetail;
